import { Controller } from '@hotwired/stimulus';

// A lightweight Stimulus controller to drive TipTap mention suggestions UI,
// reusing the same markup/classes/keyboard behavior as comment_mentions_controller.
// Public API (called from TipTap suggestion renderer):
// - open({ rect, query, onSelect, editorDom })
// - updatePosition(rect)
// - onKeyDown(event)
// - hide()
// - isOpen()
export default class extends Controller {
  static values = {
    url: String
  };

  connect() {
    this.suggestions = [];
    this.activeIndex = -1;
    this.onSelect = null;
    this.editorDom = null;
    this.abortController = null;

    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.boundDocumentClick, true);

    this.createSuggestionsElement();

    // Expose instance for the React component to call
    this.element.tiptapMentionsController = this;
  }

  disconnect() {
    document.removeEventListener('click', this.boundDocumentClick, true);
    this.destroySuggestionsElement();
    if (this.abortController) this.abortController.abort();
    if (this.element.tiptapMentionsController === this) {
      this.element.tiptapMentionsController = null;
    }
  }

  // API
  open({ rect, query, onSelect, editorDom }) {
    this.onSelect = onSelect;
    this.editorDom = editorDom || null;
    this.updatePosition(rect);
    this.performFetch((query || '').trim());
  }

  updatePosition(rect) {
    if (!rect || !this.suggestionsElement) return;
    const r = typeof rect === 'function' ? rect() : rect;
    if (!r) return;
    Object.assign(this.suggestionsElement.style, {
      position: 'fixed',
      left: `${r.left}px`,
      top: `${r.bottom + 8}px`,
      right: 'auto',
      minWidth: `${Math.max(240, r.width)}px`,
      maxWidth: '480px',
      zIndex: 9999
    });
  }

  onKeyDown(event) {
    if (!this.isOpen()) return false;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveSelection(1);
        return true;
      case 'ArrowUp':
        event.preventDefault();
        this.moveSelection(-1);
        return true;
      case 'Enter':
        event.preventDefault();
        this.chooseActiveSuggestion();
        return true;
      case 'Tab':
        this.chooseActiveSuggestion();
        event.preventDefault();
        return true;
      case 'Escape':
        this.hide();
        event.preventDefault();
        return true;
      default:
        return false;
    }
  }

  hide() {
    if (!this.suggestionsElement) return;
    this.suggestionsElement.classList.add('hidden');
    this.suggestionsElement.innerHTML = '';
    this.suggestions = [];
    this.activeIndex = -1;
  }

  isOpen() {
    return this.suggestionsElement && !this.suggestionsElement.classList.contains('hidden');
  }

  // Internals
  createSuggestionsElement() {
    this.suggestionsElement = document.createElement('div');
    this.suggestionsElement.className = 'mention-suggestions hidden';
    document.body.appendChild(this.suggestionsElement);

    this.boundSuggestionClick = (e) => {
      const button = e.target.closest('[data-index]');
      if (!button) return;
      e.preventDefault();
      const index = Number.parseInt(button.dataset.index, 10);
      this.insertFromIndex(index);
    };
    this.suggestionsElement.addEventListener('click', this.boundSuggestionClick);
  }

  destroySuggestionsElement() {
    if (!this.suggestionsElement) return;
    this.suggestionsElement.removeEventListener('click', this.boundSuggestionClick);
    if (this.suggestionsElement.parentNode) this.suggestionsElement.parentNode.removeChild(this.suggestionsElement);
    this.suggestionsElement = null;
  }

  handleDocumentClick(e) {
    const insideMenu = this.suggestionsElement && this.suggestionsElement.contains(e.target);
    const insideEditor = this.editorDom && this.editorDom.contains && this.editorDom.contains(e.target);
    if (!insideMenu && !insideEditor) {
      this.hide();
    }
  }

  moveSelection(delta) {
    if (!this.suggestions.length) return;
    const total = this.suggestions.length;
    this.activeIndex = (this.activeIndex + delta + total) % total;
    this.highlightActive();
  }

  highlightActive() {
    if (!this.suggestionsElement) return;
    Array.from(this.suggestionsElement.querySelectorAll('[data-index]')).forEach((btn, idx) => {
      if (idx === this.activeIndex) btn.classList.add('is-active');
      else btn.classList.remove('is-active');
    });
  }

  chooseActiveSuggestion() {
    if (this.activeIndex < 0 || this.activeIndex >= this.suggestions.length) {
      this.hide();
      return;
    }
    this.insertFromIndex(this.activeIndex);
  }

  insertFromIndex(index) {
    const user = this.suggestions[index];
    if (!user) return;
    if (typeof this.onSelect === 'function') {
      this.onSelect(user);
    }
    this.hide();
  }

  performFetch(query) {
    if (!this.hasUrlValue) return;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const url = new URL(this.urlValue, window.location.origin);
    if (query) url.searchParams.set('q', query);

    fetch(url.toString(), { signal: this.abortController.signal, headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        const users = Array.isArray(data) ? data : [];
        this.renderSuggestions(users);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error('Mention fetch failed', err);
      });
  }

  renderSuggestions(users) {
    this.suggestions = users;
    this.activeIndex = users.length ? 0 : -1;

    if (!users.length) {
      this.hide();
      return;
    }

    const items = users
      .map((user, index) => `
        <button type="button" class="mention-suggestion" data-index="${index}">
          <span class="mention-suggestion__name">${this.escapeHtml(user.name || 'Unknown User')}</span>
          <span class="mention-suggestion__email">${this.escapeHtml(user.email)}</span>
        </button>
      `)
      .join('');

    this.suggestionsElement.innerHTML = items;
    this.suggestionsElement.classList.remove('hidden');
    this.highlightActive();
  }

  escapeHtml(s) {
    return (s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
